import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import localStore from '@shared/utils/localStore';
import { TagEditType } from '@store/services/tagListApi';

export interface DetailTab {
  id: string;
  info?: {
    tagNum: string;
    username: string;
    storedAt: string;
    pkgsNum: number;
  };
}

interface TagDetailState {
  detailTabs: DetailTab[];
  activeTabId: string | null;
  callingTags: string[];
  tagEdit: {
    tagId?: number;
    visible: boolean;
    tagEditType?: TagEditType;
  };
}

const initialState: TagDetailState = {
  detailTabs: localStore.getAllCurrentTabs(),
  activeTabId: null,
  callingTags: localStore.getCallingTag(),
  tagEdit: {
    tagId: undefined,
    visible: false,
    tagEditType: undefined,
  },
};

const tagDetailSlice = createSlice({
  name: 'tagDetail',
  initialState,
  reducers: {
    showTagEdit: (state, action: PayloadAction<{ id: number; tagEditType: TagEditType }>) => {
      state.tagEdit.visible = true;
      state.tagEdit.tagId = action.payload.id;
      state.tagEdit.tagEditType = action.payload.tagEditType;
    },
    hideTagEdit: state => {
      state.tagEdit.visible = false;
      state.tagEdit.tagId = undefined;
      state.tagEdit.tagEditType = undefined;
    },
    updateTagEditType: (state, action: PayloadAction<{ tagEditType: TagEditType }>) => {
      state.tagEdit.tagEditType = action.payload.tagEditType;
    },
    updateDetailTabInfo: (
      state,
      action: PayloadAction<{ id: string; info: DetailTab['info'] }>,
    ) => {
      const tabToUpdate = state.detailTabs.find(tab => tab.id === action.payload.id);
      if (tabToUpdate) {
        tabToUpdate.info = action.payload.info;
      }
    },
    addDetailTab: (state, action: PayloadAction<DetailTab>) => {
      const existingTab = state.detailTabs.find(tab => tab.id === action.payload.id);
      if (!existingTab) {
        state.detailTabs.push(action.payload);
        state.activeTabId = action.payload.id;
        localStore.setAllCurrentTabs(state.detailTabs);
      } else {
        state.activeTabId = existingTab.id;
      }
    },
    removeDetailTab: (state, action: PayloadAction<string>) => {
      const newTabs = state.detailTabs.filter(tab => tab.id !== action.payload);
      if (state.activeTabId === action.payload) {
        state.activeTabId = newTabs.length > 0 ? newTabs[0].id : null;
      }
      state.detailTabs = newTabs;
      localStore.setAllCurrentTabs(state.detailTabs);
    },
    removeAllTabs: state => {
      state.detailTabs = [];
      state.activeTabId = null;
      localStore.setAllCurrentTabs([]);
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTabId = action.payload;
    },
    addCallingTag: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      if (!state.callingTags.includes(id)) {
        state.callingTags.push(id);
        localStore.setCallingTag(state.callingTags);
      }
    },
    removeCallingTag: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      const newCallingTags = state.callingTags.filter(tagId => tagId !== id);
      state.callingTags = newCallingTags;
      localStore.setCallingTag(newCallingTags);
    },
  },
});

export const {
  addDetailTab,
  removeDetailTab,
  removeAllTabs,
  setActiveTab,
  updateDetailTabInfo,
  addCallingTag,
  removeCallingTag,
  showTagEdit,
  hideTagEdit,
  updateTagEditType,
} = tagDetailSlice.actions;

export const detailTabsSelector = (state: { tagDetail: TagDetailState }) =>
  state.tagDetail.detailTabs;
export const totalTabsSelector = (state: { tagDetail: TagDetailState }) =>
  state.tagDetail.detailTabs.length;
export const activeTabSelector = (state: { tagDetail: TagDetailState }) =>
  state.tagDetail.detailTabs.find(tab => tab.id === state.tagDetail.activeTabId)?.id || null;
export const callingTagsSelector = (state: { tagDetail: TagDetailState }) =>
  state.tagDetail.callingTags;
export const tagEditSelector = (state: { tagDetail: TagDetailState }) => state.tagDetail.tagEdit;

export default tagDetailSlice.reducer;
